import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SrIcon } from "@/components/ui/SrIcon";
import { TONE_PILL } from "@/lib/widget-status";
import { MEDIA_KIND_LABELS, type CandidateSelection, type EnrichmentCandidates as Candidates } from "@/lib/equipment/enrichment";

function toggle(list: string[], value: string, on: boolean): string[] {
  return on ? [...new Set([...list, value])] : list.filter((v) => v !== value);
}

/** Forslaget i «Hent produktdata» (mockup K): bilder, dokumenter og spesifikasjoner med avkrysning. */
export function EnrichmentCandidates({
  candidates,
  selection,
  onChange,
}: {
  candidates: Candidates;
  selection: CandidateSelection;
  onChange: (s: CandidateSelection) => void;
}) {
  const set = (patch: Partial<CandidateSelection>) => onChange({ ...selection, ...patch });
  // Bilder som ikke laster (fjernet hos kilden, eller ikke et bilde) skjules og velges bort.
  const [broken, setBroken] = useState<Set<string>>(new Set());
  const onBroken = (url: string) => {
    setBroken((b) => new Set(b).add(url));
    if (selection.images.includes(url)) set({ images: selection.images.filter((u) => u !== url) });
  };
  const nothingFound = candidates.images.length === 0 && candidates.documents.length === 0 && candidates.specs.length === 0;

  return (
    <div className="space-y-5">
      {candidates.warnings.map((w) => (
        <p key={w} className={`flex gap-2 rounded-md px-3 py-2 text-sm ${TONE_PILL.near}`}>
          <SrIcon name="alert-triangle" className="h-4 w-4 shrink-0 mt-0.5" />
          {w}
        </p>
      ))}
      {nothingFound && <p className="text-sm text-muted-foreground">Fant ingen bilder, dokumenter eller spesifikasjoner.</p>}

      {candidates.images.length > 0 && (
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Bilder</legend>
          <div className="flex flex-wrap gap-3">
            {candidates.images.map((img, i) => {
              if (broken.has(img.url)) return null;
              const checked = selection.images.includes(img.url);
              return (
                <label
                  key={img.url}
                  className={`relative block w-[140px] cursor-pointer rounded-md border bg-muted p-1 ${checked ? "outline outline-2 outline-primary" : ""}`}
                >
                  <img src={img.url} alt={`Forslag ${i + 1} fra ${img.host}`} className="h-[96px] w-full object-contain" loading="lazy" referrerPolicy="no-referrer" onError={() => onBroken(img.url)} />
                  <span className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => set({ images: toggle(selection.images, img.url, v === true) })}
                      aria-label={`Velg bilde ${i + 1}`}
                    />
                    {img.source === "manufacturer" ? "Produsent" : img.host}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      )}

      {candidates.documents.length > 0 && (
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Dokumenter</legend>
          <ul className="divide-y rounded-md border">
            {candidates.documents.map((doc) => (
              <li key={doc.url} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <label className="flex min-w-0 items-center gap-2.5 cursor-pointer">
                  <Checkbox
                    checked={selection.documents.includes(doc.url)}
                    onCheckedChange={(v) => set({ documents: toggle(selection.documents, doc.url, v === true) })}
                    aria-label={`Velg ${doc.title}`}
                  />
                  <span className="truncate">{doc.title}</span>
                </label>
                <span className="flex shrink-0 items-center gap-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${
                      doc.source === "manufacturer" ? "bg-primary/10 text-primary" : TONE_PILL.near
                    }`}
                  >
                    {MEDIA_KIND_LABELS[doc.kind]} · {doc.source === "manufacturer" ? doc.host : `Ikke produsent · ${doc.host}`}
                  </span>
                  <a href={doc.url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" aria-label={`Åpne ${doc.title} hos kilden`}>
                    <SrIcon name="external-link" className="h-4 w-4" />
                  </a>
                </span>
              </li>
            ))}
          </ul>
        </fieldset>
      )}

      {candidates.specs.length > 0 && (
        <fieldset className="space-y-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="enrich-specs"
              checked={selection.includeSpecs}
              onCheckedChange={(v) => set({ includeSpecs: v === true })}
            />
            <Label htmlFor="enrich-specs" className="text-sm font-medium">Spesifikasjoner</Label>
          </div>
          {selection.includeSpecs && (
            <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
              {selection.specs.map((row, i) => (
                <div key={i} className="grid grid-cols-[2fr_3fr] gap-2">
                  <Input
                    value={row.label}
                    aria-label={`Spesifikasjon ${i + 1}, navn`}
                    className="h-8 text-sm text-muted-foreground"
                    onChange={(e) => set({ specs: selection.specs.map((s, j) => (j === i ? { ...s, label: e.target.value } : s)) })}
                  />
                  <Input
                    value={row.value}
                    aria-label={`Spesifikasjon ${i + 1}, verdi`}
                    className="h-8 text-sm"
                    onChange={(e) => set({ specs: selection.specs.map((s, j) => (j === i ? { ...s, value: e.target.value } : s)) })}
                  />
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">Kan redigeres før lagring. Tomme rader lagres ikke.</p>
        </fieldset>
      )}

      {candidates.description && (
        <div className="flex items-start gap-2">
          <Checkbox
            id="enrich-description"
            checked={selection.includeDescription}
            onCheckedChange={(v) => set({ includeDescription: v === true })}
          />
          <Label htmlFor="enrich-description" className="text-sm font-normal leading-snug">
            <span className="font-medium">Beskrivelse:</span> {candidates.description}
          </Label>
        </div>
      )}

      {candidates.product_url && (
        <p className="text-xs text-muted-foreground">
          Kilde:{" "}
          <a href={candidates.product_url} target="_blank" rel="noreferrer" className="underline">
            {candidates.product_url}
          </a>
        </p>
      )}
    </div>
  );
}
